import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { accountantInfoService, AccountantInfoRec } from '../../../../services/accountantInfo';
import { vaultService } from '../../../../services/vaultService';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('AccountantInfoData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const AccountantInfoDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();

  const [accountantsData, setAccountantsData] = useState<AccountantInfoRec[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadAccountantsData();
  }, [targetUserId]);

  const loadAccountantsData = async () => {
    try {
      setLoading(true);
      const data = await accountantInfoService.getAccountantInfo(targetUserId);
      logger.debug('[Accountants Data Debug] Loaded all accountant entries:', data);

      if (data && Array.isArray(data) && data.length > 0) {
        // Load portal credentials for all accountants (only for the owner, not viewOnly mode)
        const accountantsWithCredentials = await Promise.all(
          data.map(async (accountant) => {
            // Skip loading portal credentials in viewOnly mode - they are protected until EOL
            if (!viewOnly || isDeathVerifiedForSelectedUser) {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for accountant ID:', accountant.id);
                const portalData = await vaultService.getPortalCredentials(accountant.id, 'accountant');
                if (portalData) {
                  return {
                    ...accountant,
                    username: portalData.username,
                    password: portalData.password,
                  };
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for accountant:', accountant.id, vaultError);
              }
            }
            return accountant;
          })
        );

        logger.debug('[Accountants Data Debug] Setting all accountant data entries:', accountantsWithCredentials);
        setAccountantsData(accountantsWithCredentials);
      } else {
        logger.debug('[Accountants Data Debug] No accountant entries found or invalid data format');
        setAccountantsData([]);
      }
    } catch (error) {
      logger.error('Error loading accountants data:', error);
      Alert.alert('Error', 'Failed to load accountant information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleEditAccountant = (accountant: AccountantInfoRec) => {
    navigation.navigate('AccountantInfoInput', {
      accountantId: accountant.id,
      isEdit: true
    });
  };

  const handleDeleteAccountant = async (accountant: AccountantInfoRec) => {
    Alert.alert('Delete', 'Are you sure you want to delete this accountant entry?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await accountantInfoService.deleteAccountantInfo(accountant.id);

            // Reload data after deletion
            await loadAccountantsData();

            // Check if there are any remaining accountant entries
            const remainingData = await accountantInfoService.getAllAccountantInfo();
            if (!remainingData || remainingData.length === 0) {
              await tasksService.resetTask('accountant');
              navigation.navigate('FinanceChecklist');
            }

            Alert.alert('Deleted', 'Accountant information deleted.');
          } catch (e) {
            logger.error('Delete accountant failed:', e);
            Alert.alert('Error', 'Failed to delete accountant information');
          }
        }
      },
    ]);
  };

  const handleAddAccountant = () => {
    navigation.navigate('AccountantInfoInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Accountant Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading accountant information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!accountantsData || accountantsData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Accountant Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No accountant information found</Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddAccountant}
              >
                <Text style={styles.addButtonText}>Add Accountant Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Accountant Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {accountantsData.map((accountant, index) => (
            <View key={accountant.id || index} style={styles.accountantCard}>
              <View style={styles.cardHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.cardName}>
                    {accountant.accountantName || accountant.firmName || 'Accountant'}
                  </Text>
                  <Text style={styles.cardType}>
                    {accountant.firmName && accountant.accountantName ? accountant.firmName : 'Firm not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editCardButton}
                      onPress={() => handleEditAccountant(accountant)}
                    >
                      <Text style={styles.editCardButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteCardButton}
                      onPress={() => handleDeleteAccountant(accountant)}
                    >
                      <Text style={styles.deleteCardButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              <InfoItem label="Firm Name" value={accountant.firmName || ''} />
              <InfoItem label="Accountant Name" value={accountant.accountantName || ''} />
              <InfoItem label="Office Phone" value={formatPhoneNumber(accountant.officePhone || '')} />
              <InfoItem label="Cell Phone" value={formatPhoneNumber(accountant.cellPhone || '')} />
              <InfoItem label="Email" value={accountant.email || ''} />
              <InfoItem label="Office Address" value={accountant.officeAddress || ''} />
              <InfoItem label="Website" value={accountant.website || ''} />

              {accountant.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>
                      {showUsername[accountant.id] ? accountant.username : '••••••••••'}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setShowUsername(prev => ({ ...prev, [accountant.id]: !prev[accountant.id] }))}
                      style={styles.eyeButton}
                    >
                      <Image
                        source={showUsername[accountant.id]
                          ? require('../../../../assets/icons/Eye.png')
                          : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}

              {accountant.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>
                      {showPassword[accountant.id] ? accountant.password : '••••••••••'}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setShowPassword(prev => ({ ...prev, [accountant.id]: !prev[accountant.id] }))}
                      style={styles.eyeButton}
                    >
                      <Image
                        source={showPassword[accountant.id]
                          ? require('../../../../assets/icons/Eye.png')
                          : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}

              {accountant.hourlyRate && (
                <InfoItem label="Hourly Rate" value={`$${accountant.hourlyRate}`} />
              )}
              {accountant.annualFee && (
                <InfoItem label="Annual Fee" value={`$${accountant.annualFee}`} />
              )}
              <InfoItem label="Notes" value={accountant.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherAccountantButton}
                onPress={handleAddAccountant}
              >
                <Text style={styles.addAnotherAccountantButtonText}>Add Another Accountant</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('FinanceChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  accountantCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  cardName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  cardType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editCardButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteCardButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherAccountantButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherAccountantButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
